import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Input, Popover, Table, Tooltip, Typography } from 'antd'
import {
  BankOutlined,
  CheckOutlined,
  CloseOutlined,
  CloudOutlined,
  CloudSyncOutlined,
  CopyOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  SearchOutlined,
  SettingOutlined,
  WarningFilled,
} from '@ant-design/icons'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { useMutation } from '@tanstack/react-query'
import { useUsersQuery } from '../../hooks/useUsersQuery'
import { useRolesQuery } from '../../hooks/useRolesQuery'
import { useCanManagePermissions } from '../../hooks/useCanManagePermissions'
import { useMyClientsQuery } from 'enrollment-platform/api/persons'
import {
  accessControlApi,
  type ImpliedRoleDto,
  type UserDto,
} from '../../api/access-control.client'
import { notifyError, notifySuccess, notifyWarning } from '../../utils/notify'
import {
  COGNITO_ROLE_OPTIONS,
  formatCognitoRole,
} from '../../constants/cognito-roles'
import { cognitoWarnings } from '../../utils/email'
import UsersFilterPanel from './UsersFilterPanel'
import StatusBadge from '../../components/StatusBadge'
import ManageAccessDrawer from '../../components/ManageAccessDrawer'
import AdministeredCompaniesDrawer from '../../components/AdministeredCompaniesDrawer'
import RoleChips from '../../components/RoleChips/RoleChips'
import shared from '../../components/AccessControl.styles.module.scss'
import styles from './UsersList.module.scss'

const { Text } = Typography

export const USERS_PAGE_SIZE = 20
const VISIBLE_ROLES = 2

function CopyEmailButton({ email }: { email: string }) {
  const [copied, setCopied] = useState(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(resetTimer.current), [])

  async function copy() {
    if (!navigator.clipboard) return
    try {
      await navigator.clipboard.writeText(email)
    } catch {
      // Clipboard write was blocked (e.g. permission denied) — don't show the
      // success state for a copy that didn't happen.
      return
    }
    setCopied(true)
    clearTimeout(resetTimer.current)
    resetTimer.current = setTimeout(() => setCopied(false), 1500)
  }
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy email'}>
      <Button
        type="text"
        size="small"
        className={`${styles.copyBtn} ${copied ? styles.copyBtnCopied : ''}`}
        icon={copied ? <CheckOutlined /> : <CopyOutlined />}
        onClick={copy}
      />
    </Tooltip>
  )
}

// Clickable warning badge: the row shows *that* the profile drifted from
// Cognito, the popover breaks out *which* fields drifted and both values.
function CognitoWarningBadge({ user }: { user: UserDto }) {
  const warnings = cognitoWarnings(user)
  if (warnings.length === 0) return null

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      overlayClassName={styles.warningPopover}
      content={
        <ul className={styles.warningList}>
          {warnings.map((w) => (
            <li key={w.key} className={styles.warningItem}>
              <span className={styles.warningLabel}>
                <ExclamationCircleFilled />
                {w.label}
              </span>
              {w.detail ? (
                <span className={styles.warningDetail}>{w.detail}</span>
              ) : (
                <div className={styles.warningValues}>
                  <span className={styles.warningValueRow}>
                    <span className={styles.warningValueLabel}>Profile</span>
                    <span className={styles.warningValue}>
                      {w.key === 'role'
                        ? formatCognitoRole(w.profileValue ?? '')
                        : w.profileValue}
                    </span>
                  </span>
                  <span className={styles.warningValueRow}>
                    <span className={styles.warningValueLabel}>Cognito</span>
                    <span
                      className={`${styles.warningValue} ${styles.warningValueCognito}`}
                    >
                      {w.key === 'role'
                        ? formatCognitoRole(w.cognitoValue ?? '')
                        : w.cognitoValue}
                    </span>
                  </span>
                </div>
              )}
            </li>
          ))}
        </ul>
      }
    >
      <button
        type="button"
        className={styles.mismatchBadge}
        title={`${warnings.map((w) => w.label).join(', ')} — click for details`}
      >
        <WarningFilled />
        {warnings[0].key === 'missing'
          ? 'No Cognito'
          : `${warnings.length} mismatch`}
      </button>
    </Popover>
  )
}

interface DrawerTarget {
  cognitoUserId: string
  personUserId?: string
  email?: string
  cognitoEmail?: string
  firstName?: string
  lastName?: string
  status: string
  cognitoRole?: string
  cognitoUserRole?: string
  roleIds: string[]
  impliedRole?: ImpliedRoleDto | null
}

interface AdminDrawerTarget {
  userName: string
  companies: string[]
}

// Chip labels for the Cognito match filter. The three values are mutually
// exclusive: "missing" owns everyone without an active mirror row, so it is not
// a sub-case of "diff".
const SYNC_MATCH_LABELS: Record<'diff' | 'same' | 'missing', string> = {
  diff: 'Mismatched',
  same: 'Matched',
  missing: 'No Cognito record',
}

export default function UsersList() {
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [companyFilter, setCompanyFilter] = useState<string | undefined>()
  const [roleFilter, setRoleFilter] = useState<string[]>([])
  const [cognitoRoleFilter, setCognitoRoleFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [syncMatchFilter, setSyncMatchFilter] = useState<
    'diff' | 'same' | 'missing' | undefined
  >()
  const [page, setPage] = useState(1)
  const [target, setTarget] = useState<DrawerTarget | null>(null)
  const [adminTarget, setAdminTarget] = useState<AdminDrawerTarget | null>(null)
  const canManage = useCanManagePermissions()

  const { mutate: triggerSync, isPending: isSyncing } = useMutation({
    mutationFn: () => accessControlApi.triggerCognitoSync(),
    onSuccess: () => {
      notifySuccess(
        'Sync started',
        'Cognito users are being synced. Refresh in a moment to see updates.'
      )
    },
    onError: (error) => {
      // 409 = another admin already triggered a sync that is running/queued.
      // Not a failure the user caused, so surface it as a warning, not an error.
      // Read status defensively rather than via isAxiosError: the bundler can
      // resolve a second axios copy, making isAxiosError return false for a
      // genuine axios error.
      const status = (error as { response?: { status?: number } })?.response
        ?.status
      if (status === 409) {
        notifyWarning(
          'Sync already running',
          'A Cognito sync is already running or queued. Please wait for it to finish.'
        )
        return
      }
      notifyError(
        'Sync failed',
        'Could not start the Cognito sync. Please try again.'
      )
    },
  })

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
      setPage(1)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data: roles = [] } = useRolesQuery()
  const { data: clients = [], isLoading: clientsLoading } = useMyClientsQuery()

  const {
    data: pageData,
    isLoading,
    isError,
  } = useUsersQuery({
    search: debouncedSearch || undefined,
    clientPublicKey: companyFilter,
    roleIds: roleFilter.length > 0 ? roleFilter.join(',') : undefined,
    cognitoRole:
      cognitoRoleFilter.length > 0 ? cognitoRoleFilter.join(',') : undefined,
    status: statusFilter,
    syncMatch: syncMatchFilter,
    page,
    limit: USERS_PAGE_SIZE,
  })

  const users = pageData?.data ?? []
  const total = pageData?.total ?? 0

  const roleNameMap = useMemo(
    () => Object.fromEntries(roles.map((r) => [r.id, r.name])),
    [roles]
  )

  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: r.id, label: r.name })),
    [roles]
  )

  const clientNameMap = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c.name])),
    [clients]
  )

  // Removable summary of every active filter, rendered as chips below the search.
  const activeChips = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = []
    if (companyFilter) {
      chips.push({
        key: 'company',
        label: `Company: ${clientNameMap[companyFilter] ?? companyFilter}`,
        onRemove: () => handleCompanyChange(undefined),
      })
    }
    if (roleFilter.length > 0) {
      chips.push({
        key: 'roles',
        label: `${roleFilter.length === 1 ? 'Role' : 'Roles'}: ${roleFilter
          .map((id) => roleNameMap[id] ?? id)
          .join(', ')}`,
        onRemove: () => handleRoleFilterChange([]),
      })
    }
    if (cognitoRoleFilter.length > 0) {
      chips.push({
        key: 'cognito',
        label: `${cognitoRoleFilter.length === 1 ? 'Cognito role' : 'Cognito roles'}: ${cognitoRoleFilter
          .map((v) => formatCognitoRole(v))
          .join(', ')}`,
        onRemove: () => handleCognitoRoleChange([]),
      })
    }
    if (syncMatchFilter) {
      chips.push({
        key: 'syncMatch',
        label: `Cognito match: ${SYNC_MATCH_LABELS[syncMatchFilter]}`,
        onRemove: () => handleSyncMatchChange(undefined),
      })
    }
    if (statusFilter) {
      chips.push({
        key: 'status',
        label: `Status: ${statusFilter}`,
        onRemove: () => handleStatusChange(undefined),
      })
    }
    return chips
  }, [
    companyFilter,
    roleFilter,
    cognitoRoleFilter,
    syncMatchFilter,
    statusFilter,
    clientNameMap,
    roleNameMap,
  ])

  function handleRoleFilterChange(next: string[]) {
    setRoleFilter(next)
    setPage(1)
  }

  function handleSearchChange(value: string) {
    setSearchInput(value)
  }

  function handleCompanyChange(value: string | undefined) {
    setCompanyFilter(value)
    setPage(1)
  }

  function handleCognitoRoleChange(next: string[]) {
    setCognitoRoleFilter(next)
    setPage(1)
  }

  function handleStatusChange(value: string | undefined) {
    setStatusFilter(value)
    setPage(1)
  }

  function handleSyncMatchChange(
    value: 'diff' | 'same' | 'missing' | undefined
  ) {
    setSyncMatchFilter(value)
    setPage(1)
  }

  // Clears the panel/chip filters only. Search lives outside the Filters panel
  // and isn't counted as an active filter, so "Clear all" must leave it intact.
  function clearFilters() {
    setCompanyFilter(undefined)
    setRoleFilter([])
    setCognitoRoleFilter([])
    setStatusFilter(undefined)
    setSyncMatchFilter(undefined)
    setPage(1)
  }

  function handleTableChange(pagination: TablePaginationConfig) {
    setPage(pagination.current ?? 1)
  }

  function handleManageAccess(u: UserDto) {
    setTarget({ ...u, personUserId: u.personUserId })
  }

  function displayName(u: UserDto): string {
    return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || '—'
  }

  function handleOpenAdministered(u: UserDto) {
    setAdminTarget({
      userName: displayName(u),
      companies: u.administeredCompanies ?? [],
    })
  }

  const columns: ColumnsType<UserDto> = [
    {
      title: 'User',
      key: 'user',
      width: 260,
      ellipsis: true,
      render: (_, u) => (
        <div className={styles.userCell}>
          <span className={styles.userName}>
            {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
          </span>
          {u.email && (
            <div className={styles.emailRow}>
              {/* No tooltip: the address is already shown here. The cell
                  ellipsis-truncates, so a native title (which the browser only
                  needs for the clipped tail) carries the full value. */}
              <span className={styles.email} title={u.email}>
                {u.email}
              </span>
              <CopyEmailButton email={u.email} />
            </div>
          )}
          <CognitoWarningBadge user={u} />
        </div>
      ),
    },
    {
      title: 'Company',
      key: 'company',
      width: 160,
      ellipsis: true,
      render: (_, u) => (
        <Text type="secondary" ellipsis={{ tooltip: u.companyName }}>
          {u.companyName ?? '—'}
        </Text>
      ),
    },
    {
      title: 'Administered',
      key: 'administered',
      width: 130,
      render: (_, u) => {
        const count = u.administeredCompanies?.length ?? 0
        if (count === 0) return <Text className={styles.muted}>None</Text>
        return (
          <button
            type="button"
            className={styles.administeredPill}
            onClick={() => handleOpenAdministered(u)}
            title="View administered companies"
          >
            <BankOutlined />
            {count}
          </button>
        )
      },
    },
    {
      title: 'Roles',
      key: 'roles',
      width: 160,
      render: (_, u) => (
        <RoleChips
          roles={u.roleIds.map((id) => ({ id, name: roleNameMap[id] ?? id }))}
          impliedRole={u.impliedRole}
          visibleLimit={VISIBLE_ROLES}
        />
      ),
    },
    {
      title: 'Cognito role',
      key: 'cognitoRole',
      width: 150,
      render: (_, u) => {
        if (!u.cognitoRole) return <Text className={styles.muted}>None</Text>
        return (
          <span className={styles.cognitoChip}>
            <CloudOutlined />
            {formatCognitoRole(u.cognitoRole)}
          </span>
        )
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, u) =>
        u.status === 'none' ? (
          <Text className={styles.muted}>None</Text>
        ) : (
          <StatusBadge status={u.status} />
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      align: 'right' as const,
      render: (_, u) => (
        <button
          type="button"
          className={styles.manageBtn}
          onClick={() => handleManageAccess(u)}
        >
          {canManage ? <SettingOutlined /> : <EyeOutlined />}
          {canManage ? 'Manage access' : 'View access'}
        </button>
      ),
    },
  ]

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.searchRow}>
          <Input
            placeholder="Search by name, email"
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className={`${shared.searchInput} ${styles.search}`}
            allowClear
          />
          <UsersFilterPanel
            activeCount={activeChips.length}
            companyFilter={companyFilter}
            onCompanyChange={handleCompanyChange}
            clients={clients}
            clientsLoading={clientsLoading}
            roleFilter={roleFilter}
            onRoleChange={handleRoleFilterChange}
            roleOptions={roleOptions}
            cognitoRoleFilter={cognitoRoleFilter}
            onCognitoRoleChange={handleCognitoRoleChange}
            cognitoRoleOptions={COGNITO_ROLE_OPTIONS}
            syncMatchFilter={syncMatchFilter}
            onSyncMatchChange={handleSyncMatchChange}
            statusFilter={statusFilter}
            onStatusChange={handleStatusChange}
            onClearAll={clearFilters}
          />
          <span className={styles.count}>
            Showing <span>{users.length}</span> of <span>{total}</span>
          </span>
          {canManage && (
            <Tooltip title="Refresh the Cognito user mirror from Cognito">
              <Button
                icon={<CloudSyncOutlined />}
                loading={isSyncing}
                onClick={() => triggerSync()}
              >
                Sync Cognito users
              </Button>
            </Tooltip>
          )}
        </div>
        {activeChips.length > 0 && (
          <div className={styles.chipFilterRow}>
            {activeChips.map((chip) => (
              <span key={chip.key} className={styles.filterChip}>
                <span className={styles.filterChipLabel}>{chip.label}</span>
                <button
                  type="button"
                  title="Remove filter"
                  className={styles.filterChipRemove}
                  onClick={chip.onRemove}
                >
                  <CloseOutlined style={{ fontSize: 10 }} />
                </button>
              </span>
            ))}
            <button
              type="button"
              className={styles.clearBtn}
              onClick={clearFilters}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {isError && (
        <Alert
          type="error"
          message="Failed to load users"
          description="The server could not retrieve users. Check the server logs for details."
          style={{ marginBottom: 16 }}
        />
      )}

      <div className={shared.plainTable}>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={users}
          columns={columns}
          onChange={handleTableChange}
          tableLayout="fixed"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize: USERS_PAGE_SIZE,
            total,
            size: 'small',
            showSizeChanger: false,
            hideOnSinglePage: true,
          }}
        />
      </div>

      {target && (
        <ManageAccessDrawer
          key={target.cognitoUserId}
          cognitoUserId={target.cognitoUserId}
          personUserId={target.personUserId}
          email={target.email}
          cognitoEmail={target.cognitoEmail}
          firstName={target.firstName}
          lastName={target.lastName}
          status={target.status}
          cognitoRole={target.cognitoRole}
          cognitoUserRole={target.cognitoUserRole}
          initialRoleIds={target.roleIds}
          impliedRole={target.impliedRole}
          allRoles={roles}
          readOnly={!canManage}
          open={!!target}
          onClose={() => setTarget(null)}
        />
      )}

      {adminTarget && (
        <AdministeredCompaniesDrawer
          userName={adminTarget.userName}
          companies={adminTarget.companies}
          open={!!adminTarget}
          onClose={() => setAdminTarget(null)}
        />
      )}
    </div>
  )
}
